import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { productService } from "../../services/productService";

function Stars({ value, onChange, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange?.(n)}
          aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
          className={onChange ? "cursor-pointer" : "cursor-default"}
        >
          <Star
            size={size}
            className={n <= value ? "fill-[#c9a961] text-[#c9a961]" : "text-stone-300"}
          />
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setReviews(await productService.getReviews(productId));
    } catch (err) {
      console.error(err);
      setMessage("Impossible de charger les avis.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) load();
  }, [productId]);

  const average = reviews.length
    ? reviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) / reviews.length
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSubmitting(true);
    try {
      await productService.addReview(productId, {
        userId: user?.uid,
        rating,
        comment,
      });
      setRating(0);
      setComment("");
      setMessage("Merci pour votre avis !");
      await load();
    } catch (err) {
      setMessage(err.message || "Impossible d'envoyer l'avis.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16 border-t border-[#e5e5e5] pt-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#2d2d2d]">Avis clients</h2>
          <div className="mt-2 flex items-center gap-2 text-sm text-[#6b6b6b]">
            <Stars value={Math.round(average)} size={14} />
            <span>
              {reviews.length > 0
                ? `${average.toFixed(1)} / 5 · ${reviews.length} avis`
                : "Aucun avis pour le moment"}
            </span>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-10 space-y-4 rounded-xl bg-[#faf9f6] p-5">
          <p className="text-sm font-semibold text-[#2d2d2d]">Donnez votre avis</p>
          <Stars value={rating} onChange={setRating} size={22} />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            required
            placeholder="Partagez votre expérience avec ce produit…"
            className="w-full rounded-lg border border-[#e5e5e5] bg-white p-3 text-sm outline-none focus:border-[#c9a961]"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-[#6b6b6b]">{message}</p>
            <button
              type="submit"
              disabled={submitting || rating === 0 || !comment.trim()}
              className="rounded-md bg-[#c9a961] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-[#b8985a] disabled:opacity-40"
            >
              {submitting ? "Envoi…" : "Publier"}
            </button>
          </div>
        </form>
      ) : (
        <p className="mb-10 text-sm text-[#6b6b6b]">
          Connectez-vous pour laisser un avis.
        </p>
      )}

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-[#6b6b6b]">Chargement des avis…</p>
      ) : reviews.length === 0 ? null : (
        <ul className="space-y-6">
          {reviews.map((r) => (
            <li key={r.id} className="border-b border-[#e5e5e5] pb-6">
              <Stars value={Number(r.rating)} size={14} />
              <p className="mt-2 text-sm leading-6 text-[#2d2d2d]">{r.comment}</p>
              <p className="mt-2 text-xs text-[#6b6b6b]">
                {r.createdAt?.toDate?.().toLocaleDateString("fr-MA") || ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}